import { ColorGrid } from '@tableau/tableau-ui';
import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { DefaultFocusIndicator } from '../../styles/FocusIndicators';
import { TabStyles } from '../../styles/TabStyles';
import { makeTestId } from '../../utils/Declarative';
import { EventUtilsOptOut, PropsAndElementAttrs } from '../../utils/ReactHelpers';
import { ColorGridPreset, ColorPickerPresets } from './ColorPickerPresets';

export interface ColorSwatchProps {
  color: string;
  disabled?: boolean;
  onClick: (color: string) => void;
  selected: boolean;
  size: number;
  testId?: string;
}

const ColorSwatchSize: number = 15;
const ColorSwatchStyleName = TypeStyle.style({
  boxSizing: 'border-box',
  height: ColorSwatchSize,
  width: ColorSwatchSize,
  $nest: {
    '&:hover, &:active, &[aria-selected="true"]': { position: 'relative' },
    '&:focus-visible': { position: 'relative', ...DefaultFocusIndicator({ outlineOffset: -1, inset: true }) },
    '&[aria-selected="true"]::after': { outline: `1px solid ${TabStyles.Colors.L8}` },
    '&:hover::after, &:active::after': { outline: `1px solid ${TabStyles.Colors.L9}` },
    '&:hover::after, &:active::after, &[aria-selected="true"]::after': {
      border: `1px solid white`,
      bottom: -1,
      content: `""`,
      left: -1,
      pointerEvents: 'none',
      position: 'absolute',
      right: -1,
      top: -1,
    },
  }
});

export const DataColorAttr = 'data-color';
export const ColorSwatchWidget = React.memo(React.forwardRef<HTMLDivElement, PropsAndElementAttrs<ColorSwatchProps>>((props, ref) => {
  const { color: propsColor, className, disabled, onClick, selected, size, style, testId, ...htmlAttrs } = props;
  const color = propsColor.toLowerCase();
  const handleClick = React.useCallback(() => onClick(color), [onClick, color]);
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      {...EventUtilsOptOut}
      aria-disabled={disabled}
      aria-label={color} // not sure how useful this actually is, but otherwise screan reader reads 'blank' for every swatch, which is even worse
      aria-selected={selected}
      data-tb-test-id={makeTestId(testId, 'ColorSwatch', color)}
      {...htmlAttrs}
      className={TypeStyle.classes(className, ColorSwatchStyleName, disabled && 'disabled')}
      {...{ [DataColorAttr]: color }}
      onClick={disabled ? undefined : handleClick}
      ref={ref}
      style={{
        backgroundColor: color,
        height: size,
        width: size,
        ...style,
      }}
    />
  );
}));
ColorSwatchWidget.displayName = 'ColorSwatchWidget';

export interface ColorGridWidgetProps extends Pick<React.AriaAttributes, 'aria-label' | 'aria-labelledby'> {
  /**
   * If specified, will override the default `aria-label` provided by the widget, which varies depending on the `colorsPreset` value:
   *
   * @default "Dark Color Palette"
   * @default "Light Color Palette"
   * @default "Flow Color Palette"
   */
  'aria-label'?: string;

  /**
   * The set of columns that will be displayed in the color grid.
   * @default ColorGridPreset.Dark
   */
  colorsPreset?: ColorGridPreset;

  disabled?: boolean;

  /**
   * Callback function to notify user when a color swatch is picked
   */
  handleSwatchClicked: (color: string) => void;

  /**
   * Color that is currently selected
   */
  selectedColor?: string;

  /**
   * Test id to be attached to the dialog and its contents.
   * The suffix '-ColorGridWidget' will be automatically added
   */
  testId?: string;
}

export class ColorGridWidget extends React.PureComponent<PropsAndElementAttrs<ColorGridWidgetProps>> {
  public static defaultProps: Pick<ColorGridWidgetProps, 'colorsPreset'> = { colorsPreset: ColorGridPreset.Dark };

  public render(): React.ReactNode {
    const { colorsPreset, handleSwatchClicked, selectedColor, testId, ...htmlAttrs } = this.props

    const preset = ColorPickerPresets[colorsPreset!];

    return <ColorGrid
      {...EventUtilsOptOut}
      aria-label={preset.label()}
      data-tb-test-id={makeTestId(testId, 'ColorGrid')}
      {...htmlAttrs}
      onColorChange={handleSwatchClicked}
      palette={preset.palette}
      selectedColor={selectedColor}
    />;
  }
}
